// React hook for LiveKit Safety Checker integration
import { useEffect, useRef, useState } from 'react';
import { useParticipants, useConnectionState } from '@livekit/components-react';
import { ConnectionState } from 'livekit-client';
import { LiveKitSafetyChecker, SafetyCheckResult } from '../utils/LiveKitSafetyChecker';
import { useLiveKitProximityOptional } from '../components/LiveKitVideoDisplay/context/LiveKitProximityContext';

const EMPTY_IN_PROXIMITY = new Set<string>();

export interface UseLiveKitSafetyCheckerOptions {
  autoStart?: boolean;
  checkInterval?: number;
  autoFixEnabled?: boolean;
  enableAlerts?: boolean;
  onHealthChange?: (healthScore: number) => void;
  onIssueDetected?: (issue: any) => void;
  onFixApplied?: (fix: string) => void;
}

export const useLiveKitSafetyChecker = (options: UseLiveKitSafetyCheckerOptions = {}) => {
  const {
    autoStart = true,
    checkInterval = 3000,
    autoFixEnabled = true,
    enableAlerts = true,
    onHealthChange,
    onIssueDetected,
    onFixApplied
  } = options;

  const participants = useParticipants();
  const connectionState = useConnectionState();
  const liveKitProximity = useLiveKitProximityOptional();
  const inProximityIds = liveKitProximity?.inProximityIds ?? EMPTY_IN_PROXIMITY;
  
  const [isMonitoring, setIsMonitoring] = useState(false);
  const [lastHealthScore, setLastHealthScore] = useState(100);
  const [consecutiveFailures, setConsecutiveFailures] = useState(0);
  const [lastCheckResult, setLastCheckResult] = useState<SafetyCheckResult | null>(null);
  
  const checkerRef = useRef<LiveKitSafetyChecker | null>(null);
  const alertTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Initialize safety checker
  useEffect(() => {
    if (!checkerRef.current) {
      checkerRef.current = LiveKitSafetyChecker.getInstance();
      checkerRef.current.setAutoFixEnabled(autoFixEnabled);
    }

    if (autoStart && !isMonitoring) {
      startMonitoring();
    }

    return () => {
      if (isMonitoring) {
        stopMonitoring();
      }
    };
  }, [autoStart, autoFixEnabled]);

  // Enhanced safety checker with LiveKit integration
  const enhancedSafetyCheck = (): SafetyCheckResult => {
    const result: SafetyCheckResult = {
      timestamp: Date.now(),
      connectionState,
      totalParticipants: participants.length,
      participantsInProximity: inProximityIds.size,
      audioTracksActive: 0,
      videoTracksActive: 0,
      issues: [],
      fixesApplied: [],
      healthScore: 100
    };

    try {
      // Check connection state
      if (connectionState !== ConnectionState.Connected) {
        result.issues.push({
          type: 'connectivity',
          severity: 'critical',
          message: `Connection state is ${connectionState}, expected Connected`
        });
        result.healthScore -= 30;
      }

      // Check participants and proximity
      let participantsInProximity = 0;
      const proximityIssues: any[] = [];

      participants.forEach((participant) => {
        if (participant.isLocal) return;

        // Check if participant should be in proximity based on game state
        const shouldBeInProximity = inProximityIds.has(participant.identity);
        
        if (shouldBeInProximity) {
          participantsInProximity++;
        }

        // Check audio track subscriptions
        const audioTracks = Array.from(participant.trackPublications.values() as any)
          .filter((pub: any) => pub.kind === 'audio');

        if (audioTracks.length > 0) {
          const subscribedAudioTracks = audioTracks.filter((pub: any) => pub.isSubscribed);
          
          if (shouldBeInProximity && subscribedAudioTracks.length === 0) {
            result.issues.push({
              type: 'subscription',
              severity: 'medium',
              message: `Participant ${participant.identity} has audio but not subscribed`,
              participantId: participant.identity,
              suggestedFix: 'Subscribe to audio tracks'
            });
            result.healthScore -= 10;
          }
        }

        // Count audio/video tracks
        result.audioTracksActive += audioTracks.length;
        result.videoTracksActive += Array.from(participant.trackPublications.values() as any)
          .filter((pub: any) => pub.kind === 'video').length;
      });

      result.participantsInProximity = participantsInProximity;

      // Check for performance issues
      // Note: performance.memory is not available in all browsers
      // if (performance.memory) {
      //   const memoryUsage = performance.memory.usedJSHeapSize / performance.memory.jsHeapSizeLimit;
      //   if (memoryUsage > 0.8) {
      //     result.issues.push({
      //       type: 'performance',
      //       severity: 'medium',
      //       message: `High memory usage: ${Math.round(memoryUsage * 100)}%`
      //     });
      //     result.healthScore -= 5;
      //   }
      // }

      // Apply fixes if enabled
      if (autoFixEnabled && result.issues.length > 0) {
        const fixes = applyFixes(result.issues);
        result.fixesApplied = fixes;
        
        // Notify about fixes
        fixes.forEach(fix => {
          onFixApplied?.(fix);
        });
      }

      // Update consecutive failures
      if (result.healthScore < 50) {
        setConsecutiveFailures(prev => prev + 1);
      } else {
        setConsecutiveFailures(0);
      }

      // Notify about health changes
      if (result.healthScore !== lastHealthScore) {
        setLastHealthScore(result.healthScore);
        onHealthChange?.(result.healthScore);
      }

      // Notify about issues
      result.issues.forEach(issue => {
        onIssueDetected?.(issue);
      });

      // Handle alerts
      if (enableAlerts && consecutiveFailures >= 3) {
        showAlert();
      }

      setLastCheckResult(result);
      return result;

    } catch (error) {
      console.error('[LiveKit Safety] Error during enhanced safety check:', error);
      result.issues.push({
        type: 'connectivity',
        severity: 'critical',
        message: `Safety check failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
      result.healthScore = 0;
      return result;
    }
  };

  const applyFixes = (issues: any[]): string[] => {
    const fixesApplied: string[] = [];

    issues.forEach(issue => {
      try {
        switch (issue.type) {
          case 'proximity':
            if (issue.suggestedFix === 'Force proximity recalculation') {
              window.dispatchEvent(new CustomEvent('force-proximity-recalculation'));
              fixesApplied.push(`Forced proximity recalculation for ${issue.participantId}`);
            }
            break;

          case 'subscription':
            if (issue.suggestedFix === 'Subscribe to audio tracks' && issue.participantId) {
              const participant = participants.find(p => p.identity === issue.participantId);
              if (participant) {
                participant.trackPublications.forEach(publication => {
                  if (publication.kind === 'audio' && !publication.isSubscribed) {
                    try {
                      publication.setSubscribed(true);
                      fixesApplied.push(`Subscribed to audio for ${issue.participantId}`);
                    } catch (error) {
                      console.error(`Failed to subscribe to audio for ${issue.participantId}:`, error);
                    }
                  }
                });
              }
            }
            break;

          case 'connectivity':
            if (issue.severity === 'critical') {
              // Attempt to reconnect
              window.dispatchEvent(new CustomEvent('livekit-manual-reconnect'));
              fixesApplied.push('Attempted reconnection');
            }
            break;

          case 'performance':
            if (issue.message.includes('High memory usage')) {
              // Force garbage collection if available
              if (window.gc) {
                window.gc();
                fixesApplied.push('Forced garbage collection');
              }
            }
            break;
        }
      } catch (error) {
        console.error(`[LiveKit Safety] Failed to apply fix for ${issue.type}:`, error);
      }
    });

    return fixesApplied;
  };

  const showAlert = () => {
    if (alertTimeoutRef.current) {
      clearTimeout(alertTimeoutRef.current);
    }

    alertTimeoutRef.current = setTimeout(() => {
      console.error('🚨 LiveKit Safety Alert: Multiple consecutive failures detected!');
      console.error('Please check the console for detailed information.');
      
      // You could also show a user-facing alert here
      if (typeof window !== 'undefined' && window.alert) {
        window.alert('LiveKit connectivity issues detected. Please check the console for details.');
      }
    }, 1000);
  };

  const startMonitoring = () => {
    if (!checkerRef.current) return;

    checkerRef.current.startMonitoring(checkInterval);
    setIsMonitoring(true);

    // Override the safety check with our enhanced version
    const originalPerformSafetyCheck = checkerRef.current['performSafetyCheck'];
    checkerRef.current['performSafetyCheck'] = enhancedSafetyCheck;
  };

  const stopMonitoring = () => {
    if (!checkerRef.current) return;

    checkerRef.current.stopMonitoring();
    setIsMonitoring(false);
  };

  const forceCheck = (): SafetyCheckResult => {
    return enhancedSafetyCheck();
  };

  const setAutoFixEnabled = (enabled: boolean) => {
    if (checkerRef.current) {
      checkerRef.current.setAutoFixEnabled(enabled);
    }
  };

  return {
    isMonitoring,
    lastHealthScore,
    consecutiveFailures,
    lastCheckResult,
    startMonitoring,
    stopMonitoring,
    forceCheck,
    setAutoFixEnabled,
    shouldAlert: consecutiveFailures >= 3
  };
}; 
